/*
 * THJ Shipping Calculator v2 (2026-08-13, fix passes b-f)
 * ---------------------------------------------------------------
 * Companion to snippets/thj-shipping-calculator.liquid.
 * Read the comment block at the top of that snippet first - it explains
 * why transit times are derived from rate NAMES rather than from the API.
 *
 * Shopify's /cart/shipping_rates.json returns { presentment_name, price }
 * and nothing about delivery speed for manual flat rates. Everything about
 * dates in here is computed client-side from the configured transit map.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('thj-shipping-calculator')) return;

  var DAY_MS = 86400000;

  /* ---------------------------------------------------------------
     ZIP3 -> state. Shopify's /cart/shipping_rates.json REQUIRES a
     province for US addresses; sending a blank one errors out. So to
     keep the form to a single field we derive the state from the ZIP
     ourselves and send it silently - the customer never types it.

     Generated from the USPS ZIP dataset: 933 three-digit prefixes,
     stored as ranges. Where a prefix straddles a border (8 of the 933)
     the majority state is used. Verified at 99.94% accuracy against all
     42,789 five-digit ZIPs; the remainder fall through to the
     "confirm your state" fallback in _handle().
     --------------------------------------------------------------- */
  var ZIP3_STATES = 'AA:340;AE:090-099;AK:995-999;AL:350-352,354-369;AP:962-966;AR:716-729;AZ:850-853,855-857,859-860,863-865;CA:900-908,910-928,930-961;CO:800-816;CT:060-069;DC:200,202-205,569,888;DE:197-199;FL:320-339,341-342,344,346-347,349;GA:300-319,398-399;GU:969;HI:967-968;IA:500-516,520-528;ID:832-838;IL:600-620,622-629;IN:460-479;KS:660-662,664-679;KY:400-418,420-427;LA:700-701,703-708,710-714;MA:010-027,055;MD:206-212,214-219;ME:039-049;MI:480-499;MN:550-551,553-567;MO:630-631,633-641,644-658;MS:386-397;MT:590-599;NC:270-289;ND:580-588;NE:680-681,683-693;NH:030-038;NJ:070-089;NM:870-871,873-884;NV:889-891,893-895,897-898;NY:005,100-149;OH:430-459;OK:730-731,734-741,743-749;OR:970-979;PA:150-196;PR:006-007,009;RI:028-029;SC:290-299;SD:570-577;TN:370-385;TX:733,750-770,772-799,885;UT:840-847;VA:201,220-246;VI:008;VT:050-054,056-059;WA:980-986,988-994;WI:530-532,534-535,537-549;WV:247-268;WY:820-831';

  var ZIP3_MAP = null;

  function THJZip3Map() {
    if (ZIP3_MAP) return ZIP3_MAP;
    ZIP3_MAP = {};
    ZIP3_STATES.split(';').forEach(function (part) {
      var bits = part.split(':');
      if (bits.length !== 2) return;
      var st = bits[0];
      bits[1].split(',').forEach(function (run) {
        var ends = run.split('-');
        var a = parseInt(ends[0], 10);
        var b = ends.length > 1 ? parseInt(ends[1], 10) : a;
        for (var n = a; n <= b; n++) {
          ZIP3_MAP[('00' + n).slice(-3)] = st;
        }
      });
    });
    return ZIP3_MAP;
  }

  /* Shopify province VALUES for US addresses. We send the full name, not
     the two-letter code, because that is exactly what Shopify's own
     country/province selector submits - the one path we know the API
     accepts. The full name is also what we echo back in the heading.

     Territories (PR, GU, VI, AS, MP, FM, MH, PW) are deliberately absent:
     Shopify models those as separate COUNTRIES, not US provinces, so
     deriving them here would send an address Shopify rejects. Those ZIPs
     return '' and fall through to the country/state fallback. */
  var US_PROVINCE_NAMES = 'AL:Alabama;AK:Alaska;AZ:Arizona;AR:Arkansas;CA:California;CO:Colorado;CT:Connecticut;DE:Delaware;DC:District of Columbia;FL:Florida;GA:Georgia;HI:Hawaii;ID:Idaho;IL:Illinois;IN:Indiana;IA:Iowa;KS:Kansas;KY:Kentucky;LA:Louisiana;ME:Maine;MD:Maryland;MA:Massachusetts;MI:Michigan;MN:Minnesota;MS:Mississippi;MO:Missouri;MT:Montana;NE:Nebraska;NV:Nevada;NH:New Hampshire;NJ:New Jersey;NM:New Mexico;NY:New York;NC:North Carolina;ND:North Dakota;OH:Ohio;OK:Oklahoma;OR:Oregon;PA:Pennsylvania;RI:Rhode Island;SC:South Carolina;SD:South Dakota;TN:Tennessee;TX:Texas;UT:Utah;VT:Vermont;VA:Virginia;WA:Washington;WV:West Virginia;WI:Wisconsin;WY:Wyoming;AA:Armed Forces Americas;AE:Armed Forces Europe;AP:Armed Forces Pacific';

  var PROVINCE_NAME_MAP = null;

  function THJProvinceName(code) {
    if (!code) return '';
    if (!PROVINCE_NAME_MAP) {
      PROVINCE_NAME_MAP = {};
      US_PROVINCE_NAMES.split(';').forEach(function (pair) {
        var i = pair.indexOf(':');
        if (i > 0) PROVINCE_NAME_MAP[pair.slice(0, i)] = pair.slice(i + 1);
      });
    }
    return PROVINCE_NAME_MAP[code] || '';
  }

  /* The rate API returns the carrier's own service name, and carriers are
     inconsistent about whether their brand is in it. UPS names its products
     "UPS(R) Ground" and "UPS Next Day Air(R)", so the brand rides along.
     USPS names its product "Ground Advantage" - the carrier lives in the
     `source` field instead, which is why the cart showed a bare
     "Ground Advantage" while checkout showed "USPS Ground Advantage".
     Checkout qualifies the label; the legacy cart endpoint does not.

     So we qualify it ourselves from `source`, and only when the name does
     not already carry the brand. Tokens are matched exactly, never as
     substrings - 'usps'.indexOf('ups') is 1, which would label every USPS
     rate as UPS. */
  var CARRIER_LABELS = {
    usps: 'USPS',
    ups: 'UPS',
    ups_shipping: 'UPS',
    fedex: 'FedEx',
    dhl: 'DHL',
    dhl_express: 'DHL',
    canada_post: 'Canada Post',
    purolator: 'Purolator'
  };

  function THJCarrierLabel(source) {
    if (!source) return '';
    var raw = String(source).toLowerCase();
    if (raw === 'shopify' || raw === 'custom') return '';
    if (CARRIER_LABELS[raw]) return CARRIER_LABELS[raw];
    var tokens = raw.split(/[^a-z0-9]+/);
    for (var i = 0; i < tokens.length; i++) {
      if (CARRIER_LABELS[tokens[i]]) return CARRIER_LABELS[tokens[i]];
    }
    return '';
  }

  function THJParseTransitMap(raw) {
    var out = [];
    if (!raw) return out;
    raw.split(/[\r\n]+/).forEach(function (line) {
      var parts = line.split('|');
      if (parts.length < 3) return;
      var match = parts[0].trim();
      var min = parseInt(parts[1], 10);
      var max = parseInt(parts[2], 10);
      if (!match || isNaN(min) || isNaN(max)) return;
      out.push({ match: match.toLowerCase(), min: min, max: max });
    });
    return out;
  }

  class THJShippingCalculatorElement extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      var cfgEl = this.querySelector('[data-thj-sc-config]');
      if (!cfgEl) return;

      try {
        this.cfg = JSON.parse(cfgEl.textContent);
      } catch (e) {
        return;
      }

      this.cfg.showDates = this.cfg.showDates !== false;
      this.cfg.hidePaidWhenFree = this.cfg.hidePaidWhenFree === true;
      this.transit = THJParseTransitMap(this.cfg.transitMap);
      this.holidays = (this.cfg.holidays || '').split(',').map(function (s) { return s.trim(); });

      this.zip = this.querySelector('[name="zip"]');
      this.country = this.querySelector('[name="country"]');
      this.province = this.querySelector('[name="province"]');
      this.intlWrap = this.querySelector('[data-thj-sc-intl]');
      this.intlToggle = this.querySelector('[data-thj-sc-intl-toggle]');
      this.submit = this.querySelector('[data-thj-sc-submit]');
      this.results = this.querySelector('[data-thj-sc-results]');
      this.errorBox = this.querySelector('[data-thj-sc-error]');
      this.ratesBox = this.querySelector('[data-thj-sc-rates]');
      this.heading = this.querySelector('[data-thj-sc-heading]');
      this.list = this.querySelector('[data-thj-sc-list]');
      this.note = this.querySelector('[data-thj-sc-note]');

      this._initCountry();
      this._bind();
    }

    /* Pin country to the default and let Shopify's helper wire up provinces.
       The selects stay in the DOM (hidden) so the international fallback and
       CountryProvinceSelector both keep working. */
    _initCountry() {
      var target = (this.country && this.country.getAttribute('data-default')) || this.cfg.defaultCountry;

      if (this.country && target) {
        for (var i = 0; i < this.country.options.length; i++) {
          if (this.country.options[i].value === target) {
            this.country.selectedIndex = i;
            break;
          }
        }
      }

      setTimeout(function () {
        if (window.Shopify && Shopify.CountryProvinceSelector) {
          try {
            new Shopify.CountryProvinceSelector('thj-sc-country', 'thj-sc-province', {
              hideElement: 'thj-sc-province-container'
            });
          } catch (e) { /* non-fatal: the ZIP3 lookup covers US addresses */ }
        }
      }, 0);
    }

    _bind() {
      var self = this;

      if (this.submit) {
        this.submit.addEventListener('click', function (e) {
          e.preventDefault();
          self._estimate();
        });
      }

      if (this.zip) {
        this.zip.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            self._estimate();
          }
        });
        this.zip.addEventListener('input', function () {
          self._hideResults();
        });
      }

      if (this.country) {
        this.country.addEventListener('change', function () { self._hideResults(); });
      }

      if (this.intlToggle) {
        this.intlToggle.addEventListener('click', function (e) {
          e.preventDefault();
          self._revealIntl();
          self.intlToggle.setAttribute('hidden', '');
        });
      }
    }

    _revealIntl() {
      if (this.intlWrap) this.intlWrap.removeAttribute('hidden');
    }

    _hideResults() {
      if (this.results) this.results.setAttribute('hidden', '');
    }

    _showError(html) {
      this.results.removeAttribute('hidden');
      this.ratesBox.setAttribute('hidden', '');
      this.errorBox.removeAttribute('hidden');
      this.errorBox.innerHTML = html;
    }

    _estimate() {
      var self = this;
      var zip = (this.zip && this.zip.value || '').trim();

      if (!zip) {
        this._showError('Enter a ZIP or postal code.');
        return;
      }

      /* When the international fields are open, trust what the customer
         picked. When they are closed, ignore the selects entirely -
         CountryProvinceSelector has quietly chosen a state for us (the
         customer's default, or Alabama for a guest) and sending that
         rejects every ZIP outside it. Derive the state from the ZIP
         instead: Shopify requires a province for US addresses, so a blank
         one errors, and the customer still only types one field. */
      var intlOpen = this.intlWrap && !this.intlWrap.hasAttribute('hidden');
      var country, province, derived = false;

      if (intlOpen) {
        country = (this.country && this.country.value) || this.cfg.defaultCountry;
        province = (this.province && this.province.value) || '';
      } else {
        country = this.cfg.defaultCountry;
        province = THJProvinceName(this._stateFromZip(zip));
        derived = !!province;
      }

      /* province is the full name in both paths - which is both what the
         API wants and what we echo back in the heading. */
      var loc = { zip: zip, state: province, country: country };

      if (this.submit) {
        this.submit.setAttribute('disabled', '');
        this.submit.classList.add('is-loading');
      }

      var url = this.cfg.cartUrl + '/shipping_rates.json'
        + '?shipping_address[zip]=' + encodeURIComponent(zip)
        + '&shipping_address[country]=' + encodeURIComponent(country)
        + '&shipping_address[province]=' + encodeURIComponent(province);

      fetch(url, { credentials: 'same-origin', method: 'GET' })
        .then(function (r) { return r.json(); })
        .then(function (result) { self._handle(result, intlOpen, derived, loc); })
        .catch(function () {
          self._showError('We could not reach the shipping service. Please try again, or continue to checkout to see shipping.');
        })
        .then(function () {
          if (self.submit) {
            self.submit.removeAttribute('disabled');
            self.submit.classList.remove('is-loading');
          }
        });
    }

    _handle(result, intlOpen, derived, loc) {
      /* Shopify returns { shipping_rates: [...] } on success, or a map of
         field -> [messages] on validation failure. */
      if (!result || !result.shipping_rates) {
        var msgs = [];
        var badProvince = false;
        var badZip = false;

        Object.keys(result || {}).forEach(function (key) {
          var val = result[key];
          var text = Array.isArray(val) ? val.join(', ') : String(val);

          /* Match the FIELD NAME only. Testing the message text is a trap
             that cost us a build: Shopify says "Enter a valid ZIP code for
             Utah, United States", and /state/i matches the "State" inside
             "United States" - so every US error popped the country and
             state selects open. Do not reintroduce a text match here. */
          if (/^(province|state)$/i.test(key)) badProvince = true;
          if (/^(zip|postal_code)$/i.test(key)) badZip = true;

          msgs.push(text);
        });

        /* Reveal the selects only when they can actually help: Shopify
           asked for a province, or our ZIP3 lookup guessed a state the ZIP
           does not belong to (the ~0.06% of ZIPs on a prefix that straddles
           a border, plus every territory ZIP, which Shopify treats as its
           own country). */
        if (!intlOpen && (badProvince || badZip)) {
          this._revealIntl();
          if (this.intlToggle) this.intlToggle.setAttribute('hidden', '');
          this._showError(derived
            ? 'We could not match that ZIP code. Check it, or confirm your location below.'
            : 'Please confirm your country and state below, then check again.');
          return;
        }

        this._showError(msgs.length ? msgs.join('<br>') : 'We could not find rates for that address.');
        return;
      }

      var rates = result.shipping_rates.slice();
      if (!rates.length) {
        this._showError('No shipping options were returned for that address. Please contact us and we will sort it out.');
        return;
      }

      rates.sort(function (a, b) { return parseFloat(a.price) - parseFloat(b.price); });

      var hasFree = rates.some(function (r) { return parseFloat(r.price) === 0; });
      var suppressed = 0;

      if (this.cfg.hidePaidWhenFree && hasFree) {
        var before = rates.length;
        rates = rates.filter(function (r) { return parseFloat(r.price) === 0; });
        suppressed = before - rates.length;
      }

      this._render(rates, suppressed, loc);
    }

    _render(rates, suppressed, loc) {
      var self = this;

      this.results.removeAttribute('hidden');
      this.errorBox.setAttribute('hidden', '');
      this.errorBox.textContent = '';
      this.ratesBox.removeAttribute('hidden');
      this.list.innerHTML = '';

      /* Echo the destination back. This is the only confirmation the
         customer gets that our ZIP -> state lookup guessed right, so a
         wrong guess is visible immediately rather than silently priced. */
      var where = this._locationLabel(loc);
      this.heading.textContent = (rates.length === 1 ? 'Shipping to ' : 'Shipping options for ')
        + where + ':';

      rates.forEach(function (rate) {
        var name = self._displayName(rate);
        var price = parseFloat(rate.price);
        var eta = self._etaForRate(rate);

        var li = document.createElement('li');
        li.className = 'thj-sc__rate';

        var label = document.createElement('span');
        label.className = 'thj-sc__rate-name';
        label.textContent = name;

        var cost = document.createElement('span');
        cost.className = 'thj-sc__rate-price';
        cost.textContent = price === 0 ? 'Free' : self._money(rate.price);

        li.appendChild(label);
        li.appendChild(cost);

        if (eta) {
          var etaEl = document.createElement('span');
          etaEl.className = 'thj-sc__rate-eta';
          etaEl.textContent = eta;
          li.appendChild(etaEl);
        }

        self.list.appendChild(li);
      });

      var notes = [];
      if (this.cfg.isPersonalized && this.cfg.personalizedNote) notes.push(this.cfg.personalizedNote);
      if (suppressed > 0 && this.cfg.fasterNote) notes.push(this.cfg.fasterNote);

      if (notes.length) {
        this.note.innerHTML = notes.join('<br>');
        this.note.removeAttribute('hidden');
      } else {
        this.note.setAttribute('hidden', '');
      }
    }

    _locationLabel(loc) {
      if (!loc || !loc.zip) return 'this ZIP code';
      if (loc.country && loc.country !== this.cfg.defaultCountry) {
        return loc.zip + ', ' + loc.country;
      }
      return loc.state ? loc.zip + ', ' + loc.state : loc.zip;
    }

    /* ZIP3 -> USPS state code, or '' when we cannot tell. */
    _stateFromZip(zip) {
      var digits = String(zip).replace(/[^0-9]/g, '');
      if (digits.length < 5) return '';
      return THJZip3Map()[digits.slice(0, 3)] || '';
    }

    /* Carrier-qualified rate name, matching how checkout labels it. */
    _displayName(rate) {
      var name = rate.presentment_name || rate.name || 'Shipping';
      var brand = THJCarrierLabel(rate.source);
      if (!brand) return name;
      return name.toLowerCase().indexOf(brand.toLowerCase()) !== -1 ? name : brand + ' ' + name;
    }

    _transitFor(name) {
      var lower = String(name).toLowerCase();
      for (var i = 0; i < this.transit.length; i++) {
        if (lower.indexOf(this.transit[i].match) !== -1) return this.transit[i];
      }
      return null;
    }

    /* Delivery window for one rate - carrier data first.
       ---------------------------------------------------------------
       Shopify's checkout shows precise per-carrier dates because the
       carrier returns them, and /cart/shipping_rates.json passes them
       through on delivery_date / delivery_range / delivery_days. Using
       them means the cart and the checkout AGREE, which matters more than
       any window we could compute ourselves. The transit_map is the
       FALLBACK, for flat rates the carrier says nothing about
       (source: "shopify").

       Personalized carts skip carrier dates entirely: production time
       dwarfs transit, so an absolute date would be a lie. */
    _etaForRate(rate) {
      var name = rate.presentment_name || rate.name || '';
      var w = this._transitFor(name);

      if (!this.cfg.showDates) {
        if (!w) return '';
        return w.min === w.max
          ? 'Arrives ' + w.min + ' business day' + (w.min === 1 ? '' : 's') + ' after it ships'
          : 'Arrives ' + w.min + '-' + w.max + ' business days after it ships';
      }

      var api = this._deliveryFromRate(rate);
      if (api) return this._formatWindow(api.min, api.max);

      if (!w) return '';
      var base = this._shipBase();
      var shipMin = this._addBusinessDays(base, this.cfg.processingMin);
      var shipMax = this._addBusinessDays(base, this.cfg.processingMax);
      return this._formatWindow(
        this._addBusinessDays(shipMin, w.min),
        this._addBusinessDays(shipMax, w.max)
      );
    }

    _deliveryFromRate(rate) {
      var a = null, b = null;

      if (rate.delivery_range && rate.delivery_range.length) {
        a = this._parseYMD(rate.delivery_range[0]);
        b = this._parseYMD(rate.delivery_range[rate.delivery_range.length - 1]);
      } else if (rate.delivery_date) {
        a = b = this._parseYMD(rate.delivery_date);
      } else if (rate.delivery_days && rate.delivery_days.length) {
        var base = this._shipBase();
        var lo = Math.min.apply(null, rate.delivery_days);
        var hi = Math.max.apply(null, rate.delivery_days);
        a = this._addBusinessDays(this._addBusinessDays(base, this.cfg.processingMin), lo);
        b = this._addBusinessDays(this._addBusinessDays(base, this.cfg.processingMax), hi);
      }

      return (a && b) ? { min: a, max: b } : null;
    }

    /* Local noon, so a date string parsed as UTC can never slide a day
       backwards in a negative-offset timezone. */
    _parseYMD(str) {
      var m = String(str).match(/(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return null;
      return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10), 12, 0, 0);
    }

    _formatWindow(a, b) {
      var x = this._fmtDate(a);
      var y = this._fmtDate(b);
      return x === y ? 'Estimated arrival ' + x : 'Estimated arrival ' + x + ' – ' + y;
    }

    /* "Today" in the store's timezone, normalised to local noon so that
       adding days never trips over a DST boundary. Past the cutoff hour,
       the clock starts tomorrow. */
    _shipBase() {
      var parts;
      try {
        parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: this.cfg.timezone,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', hour12: false
        }).formatToParts(new Date());
      } catch (e) {
        var n = new Date();
        return new Date(n.getFullYear(), n.getMonth(), n.getDate(), 12, 0, 0);
      }

      var get = function (t) {
        var p = parts.filter(function (x) { return x.type === t; })[0];
        return p ? parseInt(p.value, 10) : 0;
      };

      var d = new Date(get('year'), get('month') - 1, get('day'), 12, 0, 0);
      if (get('hour') >= this.cfg.cutoffHour) d = new Date(d.getTime() + DAY_MS);
      return d;
    }

    _ymd(d) {
      var m = String(d.getMonth() + 1);
      var day = String(d.getDate());
      return d.getFullYear() + '-' + (m.length < 2 ? '0' + m : m) + '-' + (day.length < 2 ? '0' + day : day);
    }

    _isBusinessDay(d) {
      var dow = d.getDay();
      if (dow === 0 || dow === 6) return false;
      return this.holidays.indexOf(this._ymd(d)) === -1;
    }

    _addBusinessDays(from, n) {
      var d = new Date(from.getTime());
      var added = 0;
      var guard = 0;
      while (added < n && guard < 400) {
        d = new Date(d.getTime() + DAY_MS);
        guard++;
        if (this._isBusinessDay(d)) added++;
      }
      while (!this._isBusinessDay(d) && guard < 400) {
        d = new Date(d.getTime() + DAY_MS);
        guard++;
      }
      return d;
    }

    _fmtDate(d) {
      try {
        return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(d);
      } catch (e) {
        return this._ymd(d);
      }
    }

    _money(cents) {
      var value = String(cents);
      var num = value.indexOf('.') !== -1 ? parseFloat(value) : parseFloat(value) / 100;
      var fmt = this.cfg.moneyFormat || '${{amount}}';
      var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!m) return '$' + num.toFixed(2);

      var out;
      switch (m[1]) {
        case 'amount_no_decimals':
          out = Math.round(num).toLocaleString('en-US');
          break;
        case 'amount_with_comma_separator':
          out = num.toFixed(2).replace('.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          out = String(Math.round(num));
          break;
        default:
          out = num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
    }
  }

  if (window.customElements && !customElements.get('thj-shipping-calculator')) {
    customElements.define('thj-shipping-calculator', THJShippingCalculatorElement);
  }
})();
